import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { SearchBox, Button, Checkbox } from '@intuginetech/intureact';
import './login.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginSuccess } from '../../store/authSlice';
import { Grid } from '@mui/material';
import { useLoginMutation, useGetIdpDetailsQuery, useGetAccessTokenMutation, useDecodeTokenMutation } from '../../store/apiSlice';
import { useConfig } from '../../hooks/useConfig';
import { useProductConfig } from '../../context/ProductConfigContext';
import ArrowForward from '@mui/icons-material/ArrowForward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const Login = ({handleStorageChange}) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    showPassword: false,
    searchUsername: ''
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { updateConfig } = useConfig();
  const { updateProductConfig } = useProductConfig();
  const processedHashRef = useRef(null);

  const [login, { isLoading, error }] = useLoginMutation();
  const { refetch: getIdpDetails, data: idpDetailsData } = useGetIdpDetailsQuery(formData.searchUsername, {
    skip: !formData.searchUsername
  });
  const [getAccessToken] = useGetAccessTokenMutation();
  const [decodeToken] = useDecodeTokenMutation();
  
  const [idpDetails, setIdpDetails] = useState([]);

  // Memoize handlers with useCallback
  const handleUsernameChange = useCallback((value) => {
    setFormData(prev => ({
      ...prev,
      username: value,
      searchUsername: ''
    }));
  }, []);

  const handleSearch = useCallback((value) => {
    setFormData(prev => ({
      ...prev,
      username: value,
      searchUsername: value
    }));
  }, []);

  const handlePasswordVisibilityToggle = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      showPassword: !prev.showPassword
    }));
  }, []);

  const handleGoogleLogin = useCallback((idp) => {
    const domain = window.location.origin;
    const redirect_url = `${domain}/login`;
    
    // Clean up any existing state before starting new flow
    localStorage.removeItem('processed_auth_code');
    localStorage.removeItem('idp_hint');
    localStorage.removeItem('idp_details');
    
    localStorage.setItem('idp_hint', idp.idp_hint);
    localStorage.setItem('idp_details', JSON.stringify(idp));
    
    const authLoginUrl = `https://auth.intusystems.info/auth/realms/TracknTrace/protocol/openid-connect/auth?client_id=sct&redirect_uri=${redirect_url}&response_mode=fragment&scope=openid&response_type=code&kc_idp_hint=${idp.idp_hint}`;
    
    window.location.href = authLoginUrl;
  }, []);

  // Update processTokenAndNavigate to include feature list fetch
  const processTokenAndNavigate = useCallback(async (token, clientName = '', isKeycloak = false) => {
    try {
      // Check if we already have a valid token
      const currentToken = localStorage.getItem('auth_token');
      if (currentToken === token) {
        console.log("Token already exists, skipping processing");
        return;
      }

      // Set auth process flag
      localStorage.setItem('auth_process_active', 'true');
      localStorage.setItem('auth_process_time', Date.now().toString());
      // Dispatch custom event to notify components
      window.dispatchEvent(new CustomEvent('authProcessChange', { 
        detail: { isInAuthProcess: true } 
      }));

      const decodedResponse = await decodeToken({
        token,
        client_name: clientName,
        keycloak: isKeycloak
      }).unwrap();

      localStorage.setItem('auth_token', token);
      updateConfig(decodedResponse.data.config);
      localStorage.setItem('user_details', JSON.stringify({ username: decodedResponse.data?.user, email: decodedResponse.data?.email ?? decodedResponse.data?.user }));

      // Fetch and update product config after decode token
      try {
        await updateProductConfig(token);
      } catch (err) {
        console.error('Failed to fetch feature list:', err);
        // Continue with login even if feature list fetch fails
      }

      if (decodedResponse.data.config?.otp_auth) {
        navigate('/otp-verification');
        // Don't clear auth process flag for OTP auth - it will be cleared after OTP verification
      } else {
        dispatch(loginSuccess(decodedResponse.data));
        handleStorageChange();
        
        // Get the intended destination after successful authentication
        const intendedDestination = localStorage.getItem('intended_destination');
        if (intendedDestination) {
          localStorage.removeItem('intended_destination');
          console.log('Navigating to intended destination:', intendedDestination);
          navigate(intendedDestination);
        } else {
          navigate('/home');
        }
        
        // Clear auth process flag immediately after successful authentication and navigation
        localStorage.removeItem('auth_process_active');
        localStorage.removeItem('auth_process_time');
        // Dispatch custom event to notify components
        window.dispatchEvent(new CustomEvent('authProcessChange', { 
          detail: { isInAuthProcess: false } 
        }));
      }

      return decodedResponse;
    } catch (error) {
      console.error('Token processing failed:', error);
      // Clear auth process flag on error
      localStorage.removeItem('auth_process_active');
      localStorage.removeItem('auth_process_time');
      window.dispatchEvent(new CustomEvent('authProcessChange', { 
        detail: { isInAuthProcess: false } 
      }));
      throw error;
    }
  }, [decodeToken, dispatch, navigate, updateConfig, handleStorageChange, updateProductConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Removed getIdpDetails call from here
      const response = await login({
        username: formData.username.toLowerCase(),
        password: formData.password
      }).unwrap();
      
      await processTokenAndNavigate(response.token);
    } catch (err) {
      console.error('Failed to login:', err);
    }
  };

  // Update the useEffect to use the new helper function
  useEffect(() => {
    let isSubscribed = true;
    let isProcessing = false;

    const handleAuthCode = async () => {
      const hashFragment = location.hash.substring(1);
      const params = new URLSearchParams(hashFragment);
      const code = params.get('code');

      // Early return conditions
      if (!code || isProcessing || !isSubscribed) return;

      // Check if we've already processed this hash
      // if (processedHashRef.current === location.hash) {
      //   console.log("Hash already processed, skipping");
      //   return;
      // }

      // Check if we've already processed this code
      const processedCode = localStorage.getItem('processed_auth_code');
      if (processedCode === code) {
        // console.log("Code already processed, skipping processing");
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      try {
        isProcessing = true;
        const idpDetails = JSON.parse(localStorage.getItem('idp_details'));
        const domain = window.location.origin;

        // Clear any existing processed code before starting new flow
        localStorage.removeItem('processed_auth_code');
        // console.log("Cleared processed_auth_code before starting new flow");
        
        const tokenResponse = await getAccessToken({ 
          body: {
            'grant_type': 'authorization_code',
            'client_id': 'sct',
            'code': code,
            'redirect_uri': `${domain}/login`
          }           
        }).unwrap();
        console.log("Token response", tokenResponse);              
        
        localStorage.removeItem('idp_hint');
        localStorage.setItem('processed_auth_code', code);
        
        // if (isSubscribed) {
          // console.log("Processing token and navigating");
          await processTokenAndNavigate(
            tokenResponse.access_token,
            idpDetails?.client_name || '',
            !!idpDetails
          );
        // }
      } catch (error) {
        console.error('Auth flow failed:', error);
        localStorage.removeItem('idp_hint');
        localStorage.removeItem('processed_auth_code');
      } finally {
        isProcessing = false;
        // Always clean up the URL after processing (success or failure)
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    // Only run if we have a hash in the URL and haven't processed it yet
    if (location.hash && processedHashRef.current !== location.hash) {
      // console.log("Processing new hash:", location.hash);
      processedHashRef.current = location.hash;
      handleAuthCode();
    }

    // Cleanup function
    return () => {
      isSubscribed = false;
      isProcessing = false;
    };
  }, [location.hash]); // Keep only location.hash as dependency

  // Add useEffect to handle idpDetailsData changes
  useEffect(() => {
    if (idpDetailsData) {
      setIdpDetails(idpDetailsData?.data?.idp_details ?? []);
    }
  }, [idpDetailsData]);

  return (
    <Grid container>
      {/* <div className="login-container"> */}
      {
        window.innerWidth <= 768 ? '' :
      (
      <Grid item xs={12} sm={12} md={9} lg={9} xl={9} className='login-container'>
        
          {/* <div className="login-illustration"> */}
        {/* Left side illustration */}
        <img 
          src="/assets/images/logistics-illustration.svg" 
          alt="Logistics illustration" 
          className="illustration-image"
        />
      {/* </div> */}
      </Grid>
      )}
      <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
      <div className="login-form-wrapper">
        <div className="logo-container">
          <img 
            src="/assets/images/intugine-logo.svg" 
            alt="Intugine Technologies" 
            className="company-logo"
          />
          <p className="company-tagline">Logistics Through Innovation</p>
        </div>
        
        <div className="login-heading">
          <h2>Intutrack</h2>
        </div>

        {error && <div className="error-message">
          {error?.data?.message || 'Failed to login'}
        </div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username*</label>
            <SearchBox
              placeholder="lovepreet@intugine.com"
              value={formData.username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              onSearch={() => handleSearch(formData.username)}
              required
              showSearchIcon={true}
              size='medium'
              searchIcon={<ArrowForward />}
            />
          </div>

          {formData.searchUsername ? (
            <>
              <div className="form-group">
                <label>Password*</label>
                <div className="password-input-container">
                  <SearchBox
                    type={formData.showPassword ? "text" : "password"}
                    placeholder=""
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    onSearch={handlePasswordVisibilityToggle}
                    required
                    size='medium'
                    showSearchIcon={false}
                    searchIcon={formData.showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  />
                </div>
                <div className="form-options">
                  {/* <label className="remember-me">
                    <Checkbox type="checkbox" /> Remember me
                  </label> */}
                  <a href="/forgot-password" className="forgot-password">Forgot Password?</a>
                </div>
              </div>
            </>
          ) : (
            // Show only username field when no search is performed
            <div className="form-options">
              {/* <label className="remember-me">
                <input type="checkbox" /> Remember me
              </label> */}
            </div>
          )}

          <Button 
            type="submit" 
            className="login-button" 
            disabled={isLoading || !formData.password || !formData.username} 
            size='medium'
          >
            {isLoading ? 'Logging in...' : 'Log In'}
          </Button>
          
          <div className="separator">OR</div>
          
          <div className="idp-buttons-container">
            {idpDetails.length === 0 ? (
              // Show default Google login when idp_details is empty
              <Button
                type="button"
                className="idp-login-button google-login-button"
                onClick={()=>handleGoogleLogin({idp_hint:'google', idp_label:'Google'})}
                size='medium'
              >
                <img src="/assets/icons/google-icon.svg"  alt="Google Login" />
                Sign in with Google
              </Button>
            ) : (
              // Show IDP buttons from the array when available
              <div className="idp-buttons-scroll">
                {idpDetails.map((idp, index) => (
                  <Button
                    key={index}
                    type="button"
                    className="idp-login-button google-login-button"
                    onClick={() => handleGoogleLogin(idp)}
                    size='medium'
                  >
                    {idp.idp_label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </form>

        <div className="support-section">
          <img src="/assets/icons/support-icon.svg" alt="Support" />
          <div className="support-text">
            <span>Support</span>
            <a href="mailto:reachus@intugine.com">reachus@intugine.com</a>
          </div>
        </div>
      </div>
   
    </Grid>
    {/* </div> */}
    </Grid>
  );
};

export default Login; 